// Build trigger: v2026.6.7 (rebuild #10) - S3 storage IAM-role fix (backend boot). Storage now detects S3 via bucket (IAM mode), supports IAM-role auth, and mocks when truly disabled. No behavior changes for web. Verified: build + full local boot.
// Build trigger: v1.0.9 2026-05-01 (rebuild #3) — CI hardening pt.2: SSH keep-alive added to deploy-staging.yml (ServerAliveInterval 60, ServerAliveCountMax 30) so long-running silent npm/nx/docker steps don't drop the SSH connection mid-build. No journey-web behavior changes.
// Build trigger: v1.0.9 2026-05-01 (rebuild #2) — CI hardening: heredoc backtick fix in deploy-staging.yml + GitHub Actions bump (checkout v4→v5, setup-node v4→v5, expo-github-action v8→v9) to clear the Node.js 20 deprecation cliff (Sept 16 2026). No journey-web behavior changes; rebuilt for image-tag parity.
// Build trigger: v1.0.9 2026-04-30 — version lockstep bump (apps + root → 1.0.9). Companion release to citizen-mobile UX hardening; no journey-web behavior changes, rebuilt for image-tag parity. CheckUp pillar filter (CP_ENGLISH / CP_NATIVE_TARGET hidden from results UI) is now mirrored downstream in citizen-mobile so 7 user-facing pillars render across both portals.
// Build trigger: v1.0.7 — Universal Links QR handoff + /l/* landing, OpenInAppButton, /.well-known/{aasa,assetlinks.json}, dashboard Care gate on needsAssessment, dev-health redesign 2026-04-21
// Build trigger: 2026-04-24 — force journey-web redeploy for: translation shield (Node.prototype patches), JourneyBuilder A/B/C fixes (safety-net creation + error surface + gated redirect), planGating PRO-bypass for heroes_enabled, use-journey-creation defaults all 5 heroes, 9 span-wraps on Category-A translate-fragile sites.
// Build trigger: v1.0.8 2026-04-27 — CoPilot lifecycle hardening: useRestartGuide/useRetakeGuide/useDiscardGuide await invalidation; engine bypass-restore for terminal status; server-restore SKIPPED when no currentChatId (preserves initialVariables); FlowLifecycleManager cancelled-status redirect to /dashboard (single FALLBACK_BY_FLOW); JourneyInstanceContext switch-guard registry so journey switcher reuses leave-modal prompt instead of unmounting it; SCHEMA_VERSION bumped to 0034.
// Build trigger: v1.0.8 2026-04-27 (rebuild #2) — DB migrations 0032/0033/0034 made idempotent (ADD COLUMN IF NOT EXISTS, CREATE INDEX IF NOT EXISTS) after staging deploy aborted on `column resume_count already exists`. Idempotency now codified as Non-Negotiable #13 in root CLAUDE.md + canonical patterns added to packages/database/CLAUDE.md.
// Build trigger: v1.0.8 2026-04-27 (rebuild #3) — UX: QultureUp BrainAssessmentWidget wrong-answer feedback re-themed from error-state (red border + XCircle, read by users as a system bug) to coaching-state (amber soft-ring + Lightbulb + warm headline "Almost — here's the answer" + encouraging subline + neutral hero card for the correct answer). Recap row gets matching amber Lightbulb + "The answer is:" pill. CheckUpResultsView inline JourneySummaryCard pill removed (results follow the global selector — single source of truth, fewer cross-journey misreads).
// Build trigger: v1.0.8 2026-04-27 (rebuild #4) — Lifecycle modals (Resume / Restart / Retake / Discard / Leave) redesigned end-to-end: unified anatomy across all 5 heroes (intent badge + headline + progress + Keep/Lose two-column diff + reversibility chip + intent-colored confirm with arming sweep + tertiary "Stay" text button). Registry extended with `keep`/`lose`/`reversible`/`intent` per flow; legacy `preFlightItems` migrated to the diff. Framer Motion entrance (220ms scale+fade), 1s arming sweep around the confirm border, semantic intent palette (info/warning/destructive/success) — same frame, hue family shifts per action. + strategic [CP:*] logging across the 6 lifecycle surfaces (manager, engine, save, localStorage, journey context, hosts) for the parallel diagnostic pass.

// ⚠️  MUST BE THE FIRST IMPORT. The translation shield patches
// `Node.prototype.insertBefore` and `Node.prototype.removeChild` so
// that browser translators (Chrome, Safari, Firefox, extensions…)
// mutating text nodes under our feet can't crash React's commit
// phase. It has to install BEFORE React's renderer touches the DOM.
// See `./shared/translation-shield.ts` for the full reasoning.
import './shared/translation-shield';

import { StrictMode } from 'react';
import { MotionConfig } from 'framer-motion';
import { createRoot } from 'react-dom/client';
import { i18n } from '@tonnie/locale';
import { SdkProvider, AuthProvider } from '@tonnie/sdk';
import { getActiveCorrelationId } from '@/features/platform/copilot/lib/correlation-context';
import { App } from '@/core/app/App';
import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import {
  initJourneyObservability,
  getAnalyticsClient,
  getErrorClient,
} from '@/core/lib/observability';
import '@/core/styles/globals.css';

// ── Initialize observability BEFORE React renders (Part 22 §119.3) ───
initJourneyObservability();
const analyticsClient = getAnalyticsClient();
const errorClient = getErrorClient();

// ── Prune legacy CoPilot session-state localStorage keys ─────────────
// Pre-2026-04-26 the CoPilot guide cached its in-flight session under
// `copilot-guide-state-<flowId>` (no journey scope). Switching journeys
// then replayed the previous journey's chat into the new flow. The fix
// keys entries by `<flowId>::<journeyId>`; this one-shot prune drops
// the legacy ungrouped entries on app boot so they can't leak into a
// post-fix mount before their 30-min TTL expires.
import('@/features/platform/copilot/components/guide/guide-session').then(
  ({ pruneLegacySessionKeys }) => pruneLegacySessionKeys()
);

const config = {
  apiBaseUrl: (() => {
    const url = import.meta.env.VITE_API_URL;
    if (!url) {
      if (import.meta.env.PROD) {
        throw new Error('VITE_API_URL is required in production');
      }
      return 'http://localhost:4000';
    }
    return url;
  })(),
  clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '',
};

// Validate environment in production
if (import.meta.env.PROD) {
  if (!import.meta.env.VITE_API_URL) {
    console.error('[Config] VITE_API_URL not set in production build');
  }
  if (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY) {
    console.error('[Config] VITE_CLERK_PUBLISHABLE_KEY not set in production build');
  }
}

const root = createRoot(document.getElementById('root') as HTMLElement);

// StrictMode double-invokes renders and effects in development (a no-op in
// production builds). The E2E dev server runs with it too: a component that
// only works when mounted once — a lost `mutate` callback left the Journey
// Builder on "Loading…" (29 Sep) — must fail where it can be seen.

root.render(
  <StrictMode>
    <ErrorBoundary
      variant="page"
      title={i18n.t('common.applicationError')}
      errorClient={errorClient}
    >
      <SdkProvider
        config={{
          baseUrl: config.apiBaseUrl,
          // NOTE: We intentionally do NOT redirect on 401 here.
          // Route guards (ProtectedRoute / PublicOnlyRoute) already handle
          // redirecting unauthenticated users to /login via client-side
          // navigation. Using window.location.href here caused full page
          // reload loops because SDK hooks (useCurrentUser, useUserProfile,
          // useJourneys) fire API calls before Clerk has loaded a token,
          // producing 401 responses that triggered repeated hard redirects.
        }}
        // Correlation-id propagation. The CoPilot subsystem stamps a
        // fresh id at the start of every meaningful action via
        // `<CopilotCorrelationProvider>` (mounted inside <App>); we
        // expose the live id here so the SDK interceptor stamps
        // `x-trace-id` on every request. Backend logs and Sentry events
        // for the same user action share the id — pasting it into the
        // search box surfaces the entire trail end-to-end. Radar
        // 2026-04-28 — Logging Phase L-2.
        getCorrelationId={getActiveCorrelationId}
      >
        <AuthProvider
          publishableKey={config.clerkPublishableKey}
          signInUrl="/login"
          signUpUrl="/signup"
          afterSignInUrl="/dashboard"
          afterSignUpUrl="/dashboard"
        >
          {/* Every framer-motion animation honours the reader's reduced-motion
              setting: transforms and layout moves are dropped, fades kept. */}
          <MotionConfig reducedMotion="user">
            <App analyticsClient={analyticsClient} errorClient={errorClient} />
          </MotionConfig>
        </AuthProvider>
      </SdkProvider>
    </ErrorBoundary>
  </StrictMode>
);
